import { useEffect, useMemo, useState } from "react";

import { formatMoney } from "@/components/helpers/string-utils";
import { Box, Flex } from "@/components/layout/flexbox";
import { CreditPriceOption } from "@/components/pages_logged_in/services/CreditPriceOption";
import {
  generateAllPriceOptions,
  PriceUpdateConfirmationStep,
} from "@/components/pages_logged_in/services/utils";
import { PrimaryButton } from "@/components/pieces/button/PrimaryButton";
import { Segmented } from "@/components/pieces/form/Segmented";
import { StyledDialog } from "@/components/pieces/StyledDialog";
import { Avatar } from "@/components/pieces/users/Avatar";
import { INNER_BORDER_COLOR } from "@/css/constants";
import {
  ProductDetailQuery,
  SetCustomPriceOptionMutationVariables,
} from "@/graphql";
import { CoachClient } from "@/types/coaches";
import { formatCredits } from "@/utils/string";

import { DialogSection } from "./DialogSection";
import { ErrorDialogContent } from "./ErrorDialogContent";
import { SuccessDialogContent } from "./SuccessDialogContent";

type PricingBehavior = "default" | "custom";

const PRICING_BEHAVIOR_SEGMENTED_OPTIONS: {
  label: string;
  value: PricingBehavior;
}[] = [
  {
    label: "Same as Default",
    value: "default",
  },
  {
    label: "Custom Price",
    value: "custom",
  },
];

interface ClientPriceChangingDialogProps {
  client: CoachClient;
  coachProduct: ProductDetailQuery["productById"]["coachProduct"];
  isOpen: boolean;
  isLoading: boolean;
  step: PriceUpdateConfirmationStep;
  onClose: () => void;
  onConfirm: (value: SetCustomPriceOptionMutationVariables) => void;
}

export function ClientPriceChangingDialog({
  client,
  coachProduct,
  isOpen,
  isLoading,
  step,
  onClose,
  onConfirm,
}: ClientPriceChangingDialogProps) {
  const initialPricingBehavior: PricingBehavior = client?.currentPriceOption
    ? "custom"
    : "default";

  const [pricingBehavior, setPricingBehavior] = useState<PricingBehavior>(
    initialPricingBehavior,
  );

  const initialPrice =
    client?.currentPriceOption?.price || coachProduct?.coachPrice || "0.00";

  const [currentPrice, setCurrentPrice] = useState<string>(initialPrice);

  const priceOptions = useMemo(
    () => generateAllPriceOptions(coachProduct, initialPrice),
    [coachProduct, client, initialPrice],
  );

  const handleConfirm = () => {
    onConfirm({
      customerId: client.id,
      productId: coachProduct.productId || "",
      price:
        pricingBehavior === "custom"
          ? formatMoney({ cashflow: currentPrice, decimals: 2, unit: "" })
          : undefined,
      useDefault: pricingBehavior === "default",
    });
  };

  useEffect(() => {
    setCurrentPrice(
      client.currentPriceOption?.price || coachProduct.coachPrice || "0.00",
    );
  }, [coachProduct, client]);

  useEffect(() => {
    setPricingBehavior(client.currentPriceOption ? "custom" : "default");
  }, [client]);

  const mainContent = (
    <>
      <Flex
        p={3}
        mb={3}
        css={{ border: `1px solid ${INNER_BORDER_COLOR}`, borderRadius: 8 }}
        alignItems="center"
      >
        <Avatar size={20} user={client} />
        <Box ml={1}>{client.name || client.email}</Box>
      </Flex>
      <DialogSection title="Pricing Behavior">
        <Segmented
          value={pricingBehavior}
          options={PRICING_BEHAVIOR_SEGMENTED_OPTIONS}
          onChange={setPricingBehavior}
        />
      </DialogSection>
      {pricingBehavior === "custom" && (
        <DialogSection title="Select Price">
          <Box
            css={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              columnGap: 20,
              rowGap: 10,
            }}
          >
            {priceOptions.map((option, index) => (
              <Box key={index} flexGrow={1}>
                <CreditPriceOption
                  price={option.value}
                  isLocked={option.isLocked}
                  isSelected={currentPrice === option.value}
                  onClick={() => {
                    if (!option.isLocked) {
                      setCurrentPrice(option.value);
                    }
                  }}
                />
              </Box>
            ))}
          </Box>
        </DialogSection>
      )}
      <Box mt={20}>
        <PrimaryButton
          loading={isLoading}
          disabled={
            currentPrice === initialPrice &&
            pricingBehavior === initialPricingBehavior
          }
          onClick={handleConfirm}
        >
          Confirm
        </PrimaryButton>
      </Box>
    </>
  );

  const renderContent = () => {
    switch (step) {
      case PriceUpdateConfirmationStep.ERROR:
        return <ErrorDialogContent onClose={onClose} />;
      case PriceUpdateConfirmationStep.SUCCESS:
        return (
          <SuccessDialogContent onClose={onClose}>
            <div>
              Price for <b>{client.name}</b> successfully set to{" "}
              <b>
                {pricingBehavior === "default"
                  ? "default price"
                  : formatCredits(currentPrice)}
              </b>
              .
            </div>
          </SuccessDialogContent>
        );
      case PriceUpdateConfirmationStep.INITIAL:
      default:
        return mainContent;
    }
  };

  return (
    <StyledDialog
      lazy
      usePortal
      isOpen={isOpen}
      width={step === PriceUpdateConfirmationStep.INITIAL ? "650px" : "480px"}
      title="Client's Price"
      onClose={onClose}
    >
      <Box
        p={24}
        css={{
          transition: "all 1s",
        }}
      >
        {renderContent()}
      </Box>
    </StyledDialog>
  );
}
